<template>
  <div>
    <!-- Breadcrumbs -->
    <nav class="mt-5 pt-5 ps-3" aria-label="breadcrumb">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
        <li class="breadcrumb-item active" aria-current="page">Companies</li>
      </ol>
    </nav>

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5>Companies Management</h5>
              <div class="d-flex gap-2">
                 <button class="btn btn-outline-primary btn-sm" @click="loadCompanies()">
                  <i class="fa fa-refresh me-1"></i> Refresh
                </button>
              </div>
            </div>
            <div class="card-body">
              <!-- Search & Filter -->
              <div class="row mb-4">
                <div class="col-md-4">
                  <div class="input-group">
                    <span class="input-group-text"><i class="fa fa-search"></i></span>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Search by title or slug..."
                      v-model="searchQuery"
                      @input="handleSearch"
                    />
                  </div>
                </div>
              </div>

              <!-- Companies Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="table-dark">
                    <tr>
                      <th>Logo</th>
                      <th>Title</th>
                      <th>Slug</th>
                      <th>Size</th>
                      <th>Status</th>
                      <th>Billing</th>
                      <th>Last Activity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <!-- Loading State -->
                  <tbody v-if="isLoading">
                    <tr>
                      <td :colspan="8" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <!-- Empty State -->
                  <tbody v-else-if="companies.length === 0">
                    <tr>
                      <td :colspan="8" class="text-center py-5">
                        <div class="mb-3">
                          <i class="fa fa-building fa-3x text-muted"></i>
                        </div>
                        <h5 class="text-muted">No Companies Found</h5>
                      </td>
                    </tr>
                  </tbody>
                  <!-- Error State -->
                  <tbody v-else-if="error">
                    <tr>
                      <td :colspan="8" class="text-center py-5">
                        <div class="alert alert-danger">
                          <i class="fa fa-exclamation-triangle me-2"></i>{{ error }}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <!-- Data -->
                  <tbody v-else>
                    <tr v-for="company in companies" :key="company._id">
                      <td>
                        <img
                          v-if="company.logo"
                          :src="company.logo"
                          class="rounded"
                          style="width: 40px; height: 40px; object-fit: cover"
                        />
                        <div
                          v-else
                          class="rounded bg-secondary text-white d-flex align-items-center justify-content-center"
                          style="width: 40px; height: 40px"
                        >
                          <i class="fa fa-building"></i>
                        </div>
                      </td>
                      <td>
                        <div class="fw-bold text-primary" style="cursor: pointer" @click="viewDetails(company._id)">
                          {{ company.title }}
                        </div>
                      </td>
                      <td><code>{{ company.slug }}</code></td>
                      <td>{{ company.company_size }}</td>
                      <td>
                        <span :class="['badge', getStatusBadge(company.status)]">
                          {{ company.status }}
                        </span>
                      </td>
                      <td>
                         <span :class="['badge', getBillingBadge(company.billing_status)]">
                          {{ company.billing_status }}
                        </span>
                      </td>
                      <td>
                        <small class="text-muted">{{ company.last_activity_at ? formatTimeAgo(company.last_activity_at) : 'Never' }}</small>
                      </td>
                      <td>
                        <div class="btn-group">
                          <button class="btn btn-sm btn-outline-info" @click="viewDetails(company._id)" title="View Details">
                            <i class="fa fa-eye"></i>
                          </button>
                          <button
                            class="btn btn-sm btn-outline-warning"
                            @click="handleToggleStatus(company)"
                            :title="company.is_active ? 'Deactivate' : 'Activate'"
                          >
                            <i :class="['fa', company.is_active ? 'fa-pause' : 'fa-play']"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(company)" title="Delete">
                            <i class="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Footer Stats -->
            <div class="card-footer bg-light" v-if="companies.length > 0">
               <div class="d-flex justify-content-between align-items-center">
                  <div class="text-muted small">
                    Total Companies: {{ pagination.total }}
                  </div>
                  <!-- Pagination -->
                  <div class="pagination-mini">
                    <button
                      class="page-btn"
                      :disabled="!pagination.has_prev"
                      @click="handlePageChange(pagination.current_page - 1)"
                    >«</button>
                    <span class="mx-2 small">Page {{ pagination.current_page }} of {{ pagination.total_pages }}</span>
                    <button
                      class="page-btn"
                      :disabled="!pagination.has_next"
                      @click="handlePageChange(pagination.current_page + 1)"
                    >»</button>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import apiService from '@/services/apiService';
import Swal from 'sweetalert2';

interface Company {
  _id: string;
  title: string;
  slug: string;
  company_size: string;
  logo: string | null;
  status: string;
  is_active: boolean;
  billing_status: string;
  last_activity_at: string | null;
}

const router = useRouter();
const companies = ref<Company[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const searchQuery = ref('');

const pagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false
});

const loadCompanies = async (page = 1) => {
  isLoading.value = true;
  error.value = null;
  try {
    const params = {
      page,
      limit: pagination.value.per_page,
      search: searchQuery.value
    };
    const response = await apiService.getCompanies(params);
    
    // Adapt to standardizeResponse structure from apiService
    if (response.data) {
      companies.value = response.data;
      if (response.pagination) {
        pagination.value = {
          current_page: response.pagination.currentPage || response.pagination.page || page,
          per_page: response.pagination.limit || 10,
          total: response.pagination.totalItems || response.pagination.total || 0,
          total_pages: response.pagination.totalPages || response.pagination.total_pages || 1,
          has_next: (response.pagination.currentPage || page) < (response.pagination.totalPages || 1),
          has_prev: (response.pagination.currentPage || page) > 1
        };
      }
    } else if (Array.isArray(response)) {
      companies.value = response;
      pagination.value.total = response.length;
    }
  } catch (err: any) {
    error.value = err.message || 'Failed to load companies';
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = () => {
  pagination.value.current_page = 1;
  loadCompanies(1);
};

const handlePageChange = (page: number) => {
  loadCompanies(page);
};

const viewDetails = (id: string) => {
  router.push(`/admin/companies/${id}`);
};

const handleToggleStatus = async (company: Company) => {
  try {
    await apiService.toggleCompanyStatus(company._id);
    company.is_active = !company.is_active;
    company.status = company.is_active ? 'active' : 'inactive';
    Swal.fire({
      icon: 'success',
      title: 'Status Updated',
      text: `Company is now ${company.status}`,
      timer: 1500,
      showConfirmButton: false
    });
  } catch (err) {
    Swal.fire('Error', 'Failed to update status', 'error');
  }
};

const confirmDelete = async (company: Company) => {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: `You are about to soft delete ${company.title}.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    confirmButtonText: 'Yes, delete it!'
  });

  if (result.isConfirmed) {
    try {
      await apiService.deleteCompany(company._id);
      Swal.fire('Deleted!', 'Company has been deleted.', 'success');
      loadCompanies(pagination.value.current_page);
    } catch (err) {
      Swal.fire('Error', 'Failed to delete company', 'error');
    }
  }
};

const getStatusBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active': return 'bg-success';
    case 'inactive': return 'bg-warning';
    case 'suspended': return 'bg-danger';
    default: return 'bg-secondary';
  }
};

const getBillingBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
    case 'paid': return 'bg-info text-dark';
    case 'none': return 'bg-light text-dark';
    case 'overdue': return 'bg-danger';
    default: return 'bg-secondary';
  }
};

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

onMounted(() => {
  loadCompanies();
});
</script>

<style scoped>
.pagination-mini {
  display: flex;
  align-items: center;
}
.page-btn {
  border: 1px solid #dee2e6;
  background: white;
  padding: 2px 8px;
  cursor: pointer;
}
.page-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
